import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { VideoStateTool } from './VideoStateTool';

import { getIsUserAllowedAccessToColorPalettes } from 'js/src/data/account';
import { intToHex, parseHexColor } from 'js/src/libs/color-utils';
import type { RootState } from 'js/src/libs/RootState';
import { setColorOperation } from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';

export const setTemplateColorSchema = z.object({
    slot_index: z
        .number()
        .int()
        .min(0)
        .describe(
            'The active color slot to change, by its index in templateColors (e.g. 0 for [0]).',
        ),
    color: z
        .string()
        .regex(/^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/)
        .describe('The new color as a hex code (e.g. "#1f3a5b").'),
});
export type SetTemplateColorArgs = z.infer<typeof setTemplateColorSchema>;

export class SetTemplateColorTool extends VideoStateTool<typeof setTemplateColorSchema> {
    protected override readonly schema = setTemplateColorSchema;
    protected override readonly toolName = TOOL_NAMES.setTemplateColor;
    protected override readonly toolDescription =
        "Set one of the template's active color slots to a specific color, changing every element that uses that slot across the video.";

    public override getToolDisplayName(): string {
        return 'Setting template color';
    }

    public get statusIcon(): string {
        return 'palette';
    }

    public async execute(args: SetTemplateColorArgs, _context: ToolExecutionContext) {
        const { slot_index, color } = args;

        // Mirrors the editor UI: an arbitrary custom color needs the color-palettes entitlement.
        if (!getIsUserAllowedAccessToColorPalettes(this.getState())) {
            return `Error: Custom colors aren't available on this plan. Apply one of the template's named palettes with ${TOOL_NAMES.setColorPalette} instead.`;
        }

        const colors = this.luminary.template.colors;
        if (slot_index >= colors.length) {
            return `Error: slot_index ${slot_index} is out of range — the template has ${colors.length} color slot(s) (valid indices 0 to ${colors.length - 1}).`;
        }

        const colorInt = parseHexColor(color);
        if (colorInt === null) {
            return 'Error: color must be a hex string like "#1f3a5b"';
        }

        const alpha = colors[slot_index].alpha;

        const { setColor } = makeOperationsCallable(
            { setColorOperation },
            this.luminary,
            this.dispatch,
            this.getState as () => RootState,
        );
        setColor(colorInt, slot_index, alpha);

        return `Success: Set color slot [${slot_index}] to ${intToHex(colorInt)}.`;
    }
}
